import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  Upload, Download, FileSpreadsheet, Loader2, CheckCircle2, AlertTriangle,
  Users, ClipboardList, UserCheck,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => {
  const t = localStorage.getItem('savenes_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const UPLOADERS = [
  {
    key: 'patients',
    icon: Users,
    title: 'Bulk upload Patients',
    description: 'Create many patients at once. CSV or Excel. Required: first_name, last_name, date_of_birth (YYYY-MM-DD), gender.',
    uploadPath: '/api/bulk/patients',
    templatePath: '/api/bulk/patients/template.csv',
    templateName: 'patients_template.csv',
  },
  {
    key: 'form-assignments',
    icon: ClipboardList,
    title: 'Bulk assign Forms to Patients',
    description: 'One row = one form-to-patient assignment. Required: form_id, patient_mrn (must already exist in your org), due_date.',
    uploadPath: '/api/bulk/form-assignments',
    templatePath: '/api/bulk/form-assignments/template.csv',
    templateName: 'form_assignments_template.csv',
  },
  {
    key: 'form-role-assignments',
    icon: UserCheck,
    title: 'Bulk assign Forms to Roles',
    description: 'Assign a form to every active user of a role. Required: form_id, role (e.g. nurse, aide, physician), due_date.',
    uploadPath: '/api/bulk/form-role-assignments',
    templatePath: '/api/bulk/form-role-assignments/template.csv',
    templateName: 'form_role_assignments_template.csv',
  },
];

export default function BulkUploadsPage() {
  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6" data-testid="bulk-uploads-page">
      <div>
        <h1 className="text-2xl font-bold text-[#0A2342] flex items-center gap-2">
          <FileSpreadsheet className="w-5 h-5 text-[#0E7490]" /> Bulk Uploads
        </h1>
        <p className="text-sm text-[#64748B] mt-1">
          Upload CSV or Excel files to create patients in bulk and assign forms to many patients or whole roles at once.
        </p>
      </div>

      {UPLOADERS.map(u => (
        <Uploader key={u.key} {...u} />
      ))}
    </div>
  );
}

function Uploader({ key: testKey, icon: Icon, title, description, uploadPath, templatePath, templateName }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const downloadTemplate = async () => {
    try {
      const r = await axios.get(`${API_URL}${templatePath}`, {
        headers: authHeader(),
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = templateName; a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Could not download template');
    }
  };

  const upload = async () => {
    if (!file) { toast.error('Pick a file first'); return; }
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API_URL}${uploadPath}`, fd, {
        headers: { ...authHeader(), 'Content-Type': 'multipart/form-data' },
      });
      setResult(r.data);
      if (r.data?.created > 0) {
        toast.success(`Created ${r.data.created}${r.data.skipped ? ` (skipped ${r.data.skipped})` : ''}`);
      } else {
        toast.error('No rows were imported — check the errors below');
      }
      setFile(null);
      if (inputRef.current) inputRef.current.value = '';
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally { setBusy(false); }
  };

  return (
    <Card className="border-[#E2E8F0]" data-testid={`uploader-${testKey}`}>
      <CardHeader className="pb-3">
        <CardTitle className="text-base text-[#0A2342] flex items-center gap-2"><Icon className="w-4 h-4 text-[#0E7490]" /> {title}</CardTitle>
        <p className="text-xs text-[#64748B] mt-1">{description}</p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid={`template-${testKey}-btn`}>
            <Download className="w-3.5 h-3.5 mr-1" /> Download CSV template
          </Button>
          <input
            ref={inputRef}
            type="file"
            accept=".csv,.xlsx,.xls"
            onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); }}
            className="text-xs text-[#0A2342] file:mr-2 file:px-3 file:py-1.5 file:rounded-md file:border-0 file:bg-[#0E7490]/10 file:text-[#0E7490] file:font-medium"
            data-testid={`file-${testKey}-input`}
          />
          <Button onClick={upload} disabled={busy || !file} className="bg-[#0E7490] hover:bg-[#0E7490]/90 text-white" data-testid={`upload-${testKey}-btn`}>
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />} Upload
          </Button>
        </div>

        {result && (
          <div className="mt-2 rounded-md border border-[#E2E8F0] bg-[#F8FAFC] p-3 text-xs" data-testid={`result-${testKey}`}>
            <div className="flex items-center gap-3 text-sm font-medium text-[#0A2342]">
              <span className="inline-flex items-center gap-1 text-emerald-700">
                <CheckCircle2 className="w-3.5 h-3.5" /> Created: {result.created}
              </span>
              {result.skipped > 0 && (
                <span className="inline-flex items-center gap-1 text-amber-700">
                  <AlertTriangle className="w-3.5 h-3.5" /> Skipped: {result.skipped}
                </span>
              )}
              <span className="text-[#64748B]">Total rows: {result.total}</span>
            </div>
            {result.errors?.length > 0 && (
              <details className="mt-2">
                <summary className="cursor-pointer text-rose-700 font-medium">
                  {result.errors.length} row{result.errors.length === 1 ? '' : 's'} with issues
                </summary>
                <ul className="mt-2 space-y-1">
                  {result.errors.map((e, i) => (
                    <li key={i} className="text-rose-700">Row {e.row}: {e.reason}</li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}

        {testKey !== 'patients' && (
          <p className="text-[10px] text-[#94A3B8]">
            Tip: get form IDs from <Link to="/forms" className="text-[#0E7490] hover:underline">Forms</Link>.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
